import type { DeskMove } from '@/lib/deskNextMove';
import { closestGradeOfferLine } from '@/lib/closestGrade';

/** A reply is tied to the server-provided conversation route, never a company-name match. */
export function deskReplyCompanyId(move: DeskMove | null): string | null {
  if (move?.kind !== 'move' || move.key !== 'seller_reply_waiting') return null;
  if (!move.route.startsWith('/') || move.route.startsWith('//')) return null;
  const url = new URL(move.route, 'https://cannafax.ai');
  if (url.pathname !== '/messages') return null;
  const id = url.searchParams.get('with');
  return id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id) ? id : null;
}
const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : null);

/** A ready-to-edit opener for a buyer whose Buy Request one of your listings fits. Never sent without the seller pressing Send. */
export function buyRequestDraft(vars: Record<string, unknown>): string {
  const listing = str(vars.listing);
  const want = str(vars.want);
  const specs = str(vars.specs);
  const closestTo = str(vars.closestTo);
  if (closestTo) {
    const lead = closestGradeOfferLine(closestTo, str(vars.form) || null);
    const offer = (listing ? ` It's ${listing}` : '') + (specs ? ` (${specs})` : '') + (listing || specs ? '.' : '');
    return `Hi — saw your Buy Request${want ? ` (“${want}”)` : ''}. ${lead}${offer} Want me to send details and pricing?`;
  }
  const fit = (listing ? `I have ${listing}, which looks like a fit` : 'I have a listing that looks like a fit') + (specs ? ` (${specs})` : '');
  return `Hi — saw your Buy Request${want ? ` (“${want}”)` : ''}. ${fit}. Want me to send details and pricing?`;
}

/** One listing in a curated offer. Only buyer-safe fields exist on this type — never a source or grower. */
export interface OfferLine { title: string; specs: string; closestTo?: string | null; form?: string | null }

/** A ready-to-edit offer naming several fitting listings at once ("here's what I have for you"). Never sent without Send. */
export function curatedOfferDraft(want: string | null, lines: OfferLine[], note?: string | null): string {
  if (lines.length === 0) return '';
  if (lines.length === 1) return buyRequestDraft({ listing: lines[0].title, specs: lines[0].specs, want, closestTo: lines[0].closestTo ?? undefined, form: lines[0].form ?? undefined });
  const w = str(want);
  const closest = lines.find((l) => l.closestTo)?.closestTo;
  const lead = closest ? closestGradeOfferLine(closest, null) + ' ' : '';
  const list = lines.map((l, i) => `${i + 1}. ${l.title}${l.specs ? ` — ${l.specs}` : ''}${l.closestTo ? ' (closest grade)' : ''}`).join('\n');
  const extra = str(note);
  return `Hi — saw your Buy Request${w ? ` (“${w}”)` : ''}. ${lead}Here's what I have for you:\n${list}${extra ? `\n\n${extra}` : ''}\n\nWhich would you like? Happy to hold it for you.`;
}

/** Where a Next Move button goes: the server route, plus a prepared draft when the move is "message this buyer". */
export function moveDestination(move: DeskMove | null): string | null {
  if (!move || move.kind === 'caught_up') return null;
  if (move.kind !== 'move' || move.key !== 'seller_request_fillable') return move.route;
  if (!move.route.startsWith('/') || move.route.startsWith('//')) return move.route;
  const url = new URL(move.route, 'https://cannafax.ai');
  if (url.pathname !== '/messages' || !url.searchParams.get('with')) return move.route;
  url.searchParams.set('draft', buyRequestDraft((move.vars ?? {}) as Record<string, unknown>));
  return `${url.pathname}?${url.searchParams.toString()}`;
}
